interface Bucket {
  hour: string;
  treatment: string;
  count: number;
}

const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

/** Hand-drawn SVG stacked bar chart — hourly impression counts per treatment. No chart library (PLAN §1.6). */
export function ImpressionsBarChart({ buckets }: { buckets: Bucket[] }) {
  if (buckets.length === 0) {
    return <p className="muted">No impressions in this range.</p>;
  }

  const hours = [...new Set(buckets.map((b) => b.hour))].sort();
  const treatments = [...new Set(buckets.map((b) => b.treatment))].sort();
  const byHour = new Map<string, Map<string, number>>();
  for (const b of buckets) {
    const row = byHour.get(b.hour) ?? new Map<string, number>();
    row.set(b.treatment, (row.get(b.treatment) ?? 0) + b.count);
    byHour.set(b.hour, row);
  }
  const totals = hours.map((h) => [...(byHour.get(h)?.values() ?? [])].reduce((s, n) => s + n, 0));
  const maxTotal = Math.max(1, ...totals);

  const width = 720;
  const height = 260;
  const paddingLeft = 40;
  const paddingBottom = 30;
  const paddingTop = 10;
  const plotWidth = width - paddingLeft - 10;
  const plotHeight = height - paddingTop - paddingBottom;
  const barGap = 4;
  const barWidth = Math.max(2, plotWidth / hours.length - barGap);

  const yTicks = 4;
  const labelEvery = Math.max(1, Math.ceil(hours.length / 8));

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Hourly impressions per treatment"
        style={{ width: "100%", height: "auto" }}
      >
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const y = paddingTop + (plotHeight * i) / yTicks;
          const value = Math.round(maxTotal * (1 - i / yTicks));
          return (
            <g key={i}>
              <line
                x1={paddingLeft}
                x2={width - 10}
                y1={y}
                y2={y}
                stroke="var(--color-border)"
                strokeWidth={1}
              />
              <text x={paddingLeft - 6} y={y + 4} fontSize={10} textAnchor="end" fill="var(--color-text-muted)">
                {value}
              </text>
            </g>
          );
        })}

        {hours.map((hour, i) => {
          const x = paddingLeft + i * (barWidth + barGap);
          let yCursor = paddingTop + plotHeight;
          const row = byHour.get(hour);
          return (
            <g key={hour}>
              {treatments.map((t, ti) => {
                const count = row?.get(t) ?? 0;
                if (count === 0) return null;
                const barHeight = (count / maxTotal) * plotHeight;
                yCursor -= barHeight;
                return (
                  <rect
                    key={t}
                    x={x}
                    y={yCursor}
                    width={barWidth}
                    height={barHeight}
                    fill={CHART_COLORS[ti % CHART_COLORS.length]}
                  >
                    <title>
                      {hour} · {t}: {count}
                    </title>
                  </rect>
                );
              })}
              {i % labelEvery === 0 && (
                <text
                  x={x + barWidth / 2}
                  y={height - 12}
                  fontSize={9}
                  textAnchor="middle"
                  fill="var(--color-text-muted)"
                >
                  {new Date(hour).getUTCHours()}:00
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="row wrap" style={{ marginTop: "var(--space-2)" }}>
        {treatments.map((t, i) => (
          <span key={t} className="row" style={{ gap: 4 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                display: "inline-block",
                background: CHART_COLORS[i % CHART_COLORS.length],
              }}
            />
            <span className="muted">{t}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
